import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Divider,
  Chip,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SaveIcon from '@mui/icons-material/Save';
import { MenuItem, Select } from '@mui/material';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import { minimumLoading } from '../../../utils/minimumLoading';
import WaterAuthoritySubmissionPdfsSection from '../components/WaterAuthoritySubmissionPdfsSection';

import { BackflowJobGroup } from '../../model';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import axios from '../../../sharedModules/utils/axios.utils';
import AuthorityProvidedFieldsDataEntry from '../../waterAuthorityConfig/components/AuthorityProvidedFieldsDataEntry';

const WaterAuthoritySubmissionReviewPage: React.FC = () => {
  const { backflowJobGroupId } = useParams<{ backflowJobGroupId: string }>();
  const history = useHistory();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [backflowJobGroup, setBackflowJobGroup] = useState<BackflowJobGroup | null>(null);
  const [generatingPdfs, setGeneratingPdfs] = useState<boolean>(false);
  const [markingSubmitted, setMarkingSubmitted] = useState<boolean>(false);
  const [displayDropdown, setDisplayDropdown] = useState<boolean>(false);
  const [waterAuthorityLoading, setWaterAuthorityLoading] = useState<boolean>(false);
  const [waterAuthorityError, setWaterAuthorityError] = useState<string>('');
  const [optionsFetched, setOptionsFetched] = useState<boolean>(false);
  const [selectedWaterAuthority, setSelectedWaterAuthority] = useState<any>('Unknown');
  const [waterAuthorityOptionList, setWaterAuthorityOptionList] = useState<any>([]);
  const [backflowJobItems, setBackflowJobItems] = useState<any[]>([]);

  const fetchBackflowJobGroup = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get<any>(`/backflows/backflow_job_groups/${backflowJobGroupId}`);
      const data = response.data.result;
      setBackflowJobGroup(data);
      await fetchBackflowJobItems();
    } catch (err: any) {
      console.error(err);
      setError('Failed to load backflow job group details');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackflowJobItems = async () => {
    try {
      const response = await axios.get<any>('/backflows/backflow_job_items', {
        params: { 'ransack[backflow_job_group_id_eq]': backflowJobGroupId },
      });
      const items = response.data.result ?? [];
      setBackflowJobItems(items);
    } catch (err: any) {
      console.error('Failed to fetch backflow job items:', err);
    }
  };

  const generatePdfs = async () => {
    setGeneratingPdfs(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(`/backflows/water_authority_submissions`, { backflow_job_group_id: backflowJobGroupId });
      setSuccess('PDFs generated successfully!');
      await fetchBackflowJobGroup();
    } catch (err: any) {
      setError('Failed to generate some PDFs');
    } finally {
      setGeneratingPdfs(false);
    }
  };

  const markAsSubmitted = async () => {
    setMarkingSubmitted(true);
    setError('');
    setSuccess('');
    try {
      await axios.put(`/backflows/backflow_job_groups/${backflowJobGroupId}`, { status: 'submitted' });
      setSuccess('Submission marked as submitted!');
      // Redirect to submissions list page after short delay to show success message
      setTimeout(() => {
        history.push('/backflow_tests/water_authority_submissions');
      }, 1500);
    } catch (err: any) {
      console.error(err);
      setError('Failed to mark as submitted');
      setMarkingSubmitted(false);
    }
  };

  const fetchWaterAuthorityOptionList = async () => {
    try {
      const response: any = await axios.get('/pick_list_options', {
        params: { model: 'WaterAuthority' },
      });
      const fetchedOptions = response.data.result;
      fetchedOptions.unshift({ value: 'Unknown', label: 'Unknown' });
      setWaterAuthorityOptionList(fetchedOptions);
      setSelectedWaterAuthority(
        fetchedOptions.find((option: any) => option.value === backflowJobGroup?.water_authority_name)?.value ||
          'Unknown',
      );
      setOptionsFetched(true);
      setWaterAuthorityError('');
    } catch (error) {
      setWaterAuthorityError('Something went wrong fetching the Water Authority options');
    }
  };

  const updateWaterAuthority = async () => {
    try {
      await axios.patch(`/sales_estimates/customers/${backflowJobGroup?.customer_id}`, {
        customer: { water_authority_id: selectedWaterAuthority },
      });
      setWaterAuthorityError('');
      fetchBackflowJobGroup();
      setDisplayDropdown(false);
    } catch (error) {
      setWaterAuthorityError('Something went wrong updating the Water Authority');
    }
  };

  const handleUpdateClicked = () => {
    minimumLoading(updateWaterAuthority, setWaterAuthorityLoading);
  };

  const openDropdown = () => {
    setDisplayDropdown(true);
    const selectedValue =
      waterAuthorityOptionList.find((option: any) => option.label === backflowJobGroup?.water_authority_name)?.value ||
      'Unknown';
    setSelectedWaterAuthority(selectedValue);
  };

  const viewTemplate = () => {
    if (!backflowJobGroup.pdf_template_url) {
      setError('No template available for this water authority');
      return;
    }

    // Open PDF in a new tab
    window.open(backflowJobGroup.pdf_template_url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    fetchBackflowJobGroup();
  }, [backflowJobGroupId]);

  useEffect(() => {
    if (!optionsFetched && backflowJobGroup) {
      fetchWaterAuthorityOptionList();
    }
  }, [backflowJobGroup]);

  useEffect(() => {
    if (backflowJobGroup?.water_authority_name === 'Unknown') {
      setDisplayDropdown(true);
    }
  }, [backflowJobGroup?.water_authority_name]);

  const uniqueTechnicians = React.useMemo(() => {
    const technicianIds = backflowJobItems.map((item) => item.display_emp_id).filter((id) => id && id !== 'Unassigned');
    return [...new Set(technicianIds)];
  }, [backflowJobItems]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!backflowJobGroup) {
    return (
      <Box p={3}>
        <Alert severity="error">Backflow job group not found</Alert>
      </Box>
    );
  }

  let hoverStyle = {
    bgcolor: '#e9ecef',
    borderColor: '#1976d2',
    transform: 'scale(1.02)',
  };
  if (!backflowJobGroup.pdf_template_url) {
    hoverStyle = {
      bgcolor: '#f5f5f5',
      borderColor: '#f5f5f5',
      transform: 'none',
    };
  }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', minHeight: '100vh', p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PageHeader
            mainTitle="Water Authority"
            subTitle={`Set Water Authority - Order #${backflowJobGroup.order_no}`}
            backButtonLabel="WA Submissions List"
            backButtonLink="/backflow_tests/water_authority_submissions"
          />
        </Grid>

        {/* Alerts */}
        {error && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          </Grid>
        )}

        {success && (
          <Grid item xs={12}>
            <Alert severity="success" onClose={() => setSuccess('')}>
              {success}
            </Alert>
          </Grid>
        )}

        {/* Combined Job Details and Water Authority Card */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Grid container spacing={3}>
                {/* Left side - Job Details */}
                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <ReceiptIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="600">
                      Job Details
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    <Box display="flex" alignItems="flex-start" mb={2}>
                      <Typography variant="body2" fontWeight="500" sx={{ minWidth: 80 }}>
                        Order #:
                      </Typography>
                      <Typography variant="body2">{backflowJobGroup.order_no}</Typography>
                    </Box>

                    <Box display="flex" alignItems="flex-start" mb={2}>
                      <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                          Customer
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {backflowJobGroup.company_name}
                        </Typography>
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="flex-start" mb={2}>
                      <LocationOnIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                          Address
                        </Typography>
                        <Typography variant="body1">{backflowJobGroup.address1}</Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Water Authority Section */}
                  <Box mt={3}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <WaterDropIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" fontWeight="600">
                        Water Authority
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <LoadingWrapper isLoading={waterAuthorityLoading} message="Loading...">
                      <ErrorMessage message={waterAuthorityError} />
                      {displayDropdown ? (
                        <Box>
                          <Select
                            value={selectedWaterAuthority}
                            onChange={(e) => setSelectedWaterAuthority(e.target.value)}
                            fullWidth
                            sx={{ mb: 2 }}
                          >
                            <MenuItem value="">
                              <em>Choose Water Authority...</em>
                            </MenuItem>
                            {waterAuthorityOptionList?.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={handleUpdateClicked}
                            disabled={
                              selectedWaterAuthority === 'Unknown' ||
                              selectedWaterAuthority === backflowJobGroup?.water_authority_name
                            }
                          >
                            Save
                          </Button>
                        </Box>
                      ) : (
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Chip
                            label={backflowJobGroup.water_authority_name || 'Not set'}
                            color={backflowJobGroup.water_authority_name ? 'primary' : 'default'}
                            variant="outlined"
                          />
                          <Button variant="contained" color="secondary" onClick={openDropdown}>
                            Change
                          </Button>
                        </Box>
                      )}
                    </LoadingWrapper>
                  </Box>
                </Grid>

                {/* Right side - Submission Details */}
                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <PersonIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="600">
                      Submission Details
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    {backflowJobGroup.submitted_at && (
                      <Box display="flex" alignItems="flex-start" mb={2}>
                        <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                            Approved At
                          </Typography>
                          <Typography variant="body1">
                            {new Date(backflowJobGroup.submitted_at).toLocaleString('en-US', {
                              month: '2-digit',
                              day: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    <Box display="flex" alignItems="flex-start" mb={2}>
                      <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                          Technician{uniqueTechnicians.length !== 1 ? 's' : ''}
                        </Typography>
                        {uniqueTechnicians.length > 0 ? (
                          <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                            {uniqueTechnicians.map((tech, index) => (
                              <Chip key={index} label={tech} size="small" variant="outlined" />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body1" color="text.secondary">
                            No technicians assigned
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right column: Authority fields + Submission Actions */}
        <Grid item xs={12} lg={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <AuthorityProvidedFieldsDataEntry backflowJobGroup={backflowJobGroup} />
            </Grid>
            <Grid item xs={12}>
              <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="600">
                    Submission Actions
                  </Typography>

                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={generatingPdfs ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                        onClick={generatePdfs}
                        disabled={generatingPdfs}
                        fullWidth
                        size="large"
                        sx={{ py: 1.5 }}
                      >
                        {generatingPdfs ? 'Generating...' : 'Regenerate Reports'}
                      </Button>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={markingSubmitted ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                        onClick={markAsSubmitted}
                        disabled={markingSubmitted || backflowJobGroup.status !== 'approved'}
                        fullWidth
                        size="large"
                        sx={{ py: 1.5 }}
                      >
                        {markingSubmitted ? 'Marking...' : 'Mark as Submitted'}
                      </Button>
                    </Grid>
                  </Grid>

                  {backflowJobGroup.status !== 'approved' && (
                    <Box mt={3}>
                      <Alert severity="info">
                        {backflowJobGroup.status !== 'approved'
                          ? `Job group must be in 'approved' status. Current status: ${backflowJobGroup.status || 'unknown'}`
                          : 'Actions require at least one approved item.'}
                      </Alert>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* PDF List Section Header + fetch approved items */}
        <WaterAuthoritySubmissionPdfsSection backflowJobGroupId={backflowJobGroupId ?? ''} />
      </Grid>
    </Box>
  );
};

export default WaterAuthoritySubmissionReviewPage;
